/**
 * byChapters drag-to-turn viewer, <bc-turn-viewer> (markup and styles: snippets/bc-turn-viewer.liquid).
 *
 * Turns a kit through its real photographed angles. Every view is one of the product's own gallery
 * photos; nothing is generated or interpolated. The only effect between views is a short cross-fade,
 * and the viewer always comes to rest on a real photo.
 *
 * Loading, lightest first:
 * 1. The first frame is the server-rendered photo (same URL as its gallery slide). It stays lazy until step 2.
 * 2. Small copies (480 px) of the other frames load when the viewer starts to come on screen, or when its
 *    thumbnail or "Turn it" button is hovered or touched. Once the page is idle on a fast connection without
 *    Data Saver, only the four frames nearest the first one load early.
 * 3. Screen-sized copies load nearest-first once the viewer is on screen or is touched.
 * A frame that fails to load is dropped. If fewer than two frames are left, or anything else goes wrong,
 * the viewer removes its slide and thumbnail and the normal gallery carries on.
 */

/** The gallery's own srcset widths, so frames reuse the images the gallery already downloaded. */
const WIDTHS = [120, 240, 352, 480, 640, 832, 1200, 1600];
const LOW_WIDTH = 480;
const FADE_MS = 160;

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * @typedef {object} Frame
 * @property {number} id - media id of the frame (a gallery photo, or an edited frame kept in Files)
 * @property {number | null} z - gallery photo the zoom button opens (the frame itself, or the photo it was made from)
 * @property {number} w - source width
 * @property {string} alt - alt text of the photo
 * @property {string} url - CDN url with a width parameter
 * @property {number[] | null} t - alignment [scale, x, y] (x, y as fractions of the frame)
 * @property {HTMLImageElement} [el]
 * @property {Promise<void>} [lo]
 * @property {Promise<void>} [hi]
 * @property {'lo' | 'hi' | ''} [has]
 */

class BcTurnViewer extends HTMLElement {
  /** @type {Frame[]} */
  #frames = [];
  #index = 0;
  #full = false;
  #prepared = false;
  /** how many frames (nearest first) have been asked for so far */
  #primed = 0;
  #upgraded = false;
  #visible = false;
  #touched = false;
  #spun = false;
  /** @type {{ id: number, x: number, i: number, moved: boolean, fromEnd: -1 | 0 | 1 } | null} */
  #drag = null;
  /** @type {AbortController | null} */
  #ac = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  #spinTimer = 0;
  #fadeTimer = 0;
  /** @type {HTMLElement} */
  #stage;

  connectedCallback() {
    try {
      if (!this.#prepared) this.#prepare();
      this.#bind();
    } catch (error) {
      this.#fail(error);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
    this.#io?.disconnect();
    this.#io = null;
    this.#stopSpin();
  }

  /* ---------------------------------------------------------------- setup */

  #prepare() {
    const data = JSON.parse(this.querySelector("script[data-bc-turn]")?.textContent || "{}");
    this.#frames = (data.frames || []).filter((frame) => frame && typeof frame.url === "string");
    if (this.#frames.length < 2) throw new Error("bc-turn: fewer than two frames");

    this.#stage = /** @type {HTMLElement} */ (this.querySelector(".bc-turn__stage"));
    if (!this.#stage) throw new Error("bc-turn: no stage");

    this.#full = this.dataset.full === "true";
    this.#index = clamp(parseInt(this.dataset.start || "0", 10) || 0, 0, this.#frames.length - 1);

    // The server-rendered photo becomes the first frame (it keeps its srcset, so the browser
    // picks its size exactly as it does for the same photo in the gallery).
    const first = this.#frames[this.#index];
    const poster = /** @type {HTMLImageElement | null} */ (this.querySelector(".bc-turn__poster img"));
    if (poster) {
      poster.addEventListener("error", () => this.#fail(new Error("bc-turn: first frame failed")), { once: true });
      poster.className = "bc-turn__frame is-active";
      poster.alt = "";
      poster.draggable = false;
      poster.setAttribute("aria-hidden", "true");
      this.#applyAlignment(poster, first);
      this.#stage.insertBefore(poster, this.#stage.firstChild);
      first.el = poster;
      first.has = "hi";
      first.lo = first.hi = poster.complete ? Promise.resolve() : new Promise((resolve) => {
        poster.addEventListener("load", () => resolve(), { once: true });
        poster.addEventListener("error", () => resolve(), { once: true });
      });
      first.lo.then(() => (poster.dataset.frameLoaded = "hi"));
    }

    this.#prepared = true;
    this.dataset.state = "ready";
    this.#updateUi();
  }

  #bind() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const stage = this.#stage;

    stage.addEventListener("pointerdown", this.#onDown, { signal });
    stage.addEventListener("pointermove", this.#onMove, { signal });
    stage.addEventListener("pointerup", this.#onUp, { signal });
    stage.addEventListener("pointercancel", this.#onUp, { signal });
    stage.addEventListener("keydown", this.#onKey, { signal });
    stage.addEventListener("focus", () => this.#interest(true), { signal });
    stage.addEventListener("dragstart", (event) => event.preventDefault(), { signal });

    this.querySelectorAll("[data-step]").forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          this.#interest(true);
          this.#turned();
          this.#show(this.#index + Number(/** @type {HTMLElement} */ (button).dataset.step), true);
        },
        { signal }
      );
    });

    // The pill and zoom button sit over the photo: touches there must not swipe the gallery.
    this.querySelector(".bc-turn__pill")?.classList.add("swiper-no-swiping");

    // Its thumbnail shows interest before the slide is on screen.
    const thumb = this.closest("media-gallery")?.querySelector(".bc-turn-thumb");
    for (const type of ["pointerenter", "pointerdown", "focusin"]) {
      thumb?.addEventListener(type, () => this.#prime(), { signal, passive: true });
    }

    this.#bindChip(signal);

    // Loading begins as soon as any of the slide comes into view (the gallery clips off-screen
    // slides, so this fires while it is being swiped in); full size once it is half on screen.
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#prime();
          this.#visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          if (this.#visible) {
            this.#upgrade();
            this.#maybeSpin();
          } else {
            this.#stopSpin();
          }
        }
      },
      { threshold: [0, 0.5] }
    );
    this.#io.observe(this);

    // Small copies once the page is idle, on a fast connection only.
    const connection = /** @type {any} */ (navigator).connection;
    const slow = connection && (connection.saveData || /(^|-)2g$|^3g$/.test(connection.effectiveType || ""));
    if (!slow) {
      const idle = () =>
        "requestIdleCallback" in window
          ? window.requestIdleCallback(() => this.#prime(4), { timeout: 5000 })
          : setTimeout(() => this.#prime(4), 1000);
      const later = () => setTimeout(idle, 2500);
      if (document.readyState === "complete") later();
      else window.addEventListener("load", later, { once: true, signal });
    }

    document.addEventListener("visibilitychange", () => document.hidden && this.#stopSpin(), { signal });
  }

  /** The "Turn it" button over the first gallery photo: shown only on that slide; jumps to the viewer. */
  #bindChip(signal) {
    const chip = /** @type {HTMLButtonElement | null} */ (this.closest("media-gallery")?.querySelector("[data-bc-turn-chip]"));
    const carousel = /** @type {any} */ (this.closest("carousel-slider"));
    const slide = this.closest(".swiper-slide");
    if (!chip || !carousel || !slide) return;
    // Visible only while the first gallery item is on screen (watching the slide itself keeps working when the
    // theme rebuilds its carousel).
    const firstSlide = carousel.querySelector(".media-gallery__carousel-wrapper > .swiper-slide");
    if (firstSlide && firstSlide !== slide) {
      const io = new IntersectionObserver(([entry]) => (chip.hidden = !(entry && entry.intersectionRatio >= 0.6)), {
        threshold: [0, 0.6, 1],
      });
      io.observe(firstSlide);
      signal.addEventListener("abort", () => io.disconnect(), { once: true });
    }
    chip.addEventListener("pointerenter", () => this.#prime(), { signal, passive: true });
    chip.addEventListener(
      "click",
      (event) => {
        const swiper = carousel.swiperInstance;
        const index = swiper && !swiper.destroyed ? Array.from(swiper.slides || []).indexOf(slide) : -1;
        this.#interest(true);
        if (index >= 0) swiper.slideTo(index);
        else slide.scrollIntoView({ behavior: "smooth", block: "center" }); // grid layout: no carousel
        // keyboard users land on the viewer itself
        if (event.detail === 0) this.#stage.focus({ preventScroll: true });
      },
      { signal }
    );
  }

  /* -------------------------------------------------------------- loading */

  /** @param {Frame} frame @param {'lo' | 'hi'} kind */
  #url(frame, kind) {
    const width = this.#stage.clientWidth || this.clientWidth || 600;
    const dpr = clamp(window.devicePixelRatio || 1, 1, 3);
    const want = kind === "hi" ? width * dpr : Math.min(width, LOW_WIDTH);
    const size = WIDTHS.find((w) => w >= want) || WIDTHS[WIDTHS.length - 1];
    return frame.url.replace(/([?&])width=\d+/, `$1width=${size}`);
  }

  /** @param {Frame} frame */
  #frameEl(frame) {
    if (frame.el) return frame.el;
    const img = document.createElement("img");
    img.className = "bc-turn__frame";
    img.alt = "";
    img.draggable = false;
    img.decoding = "async";
    img.setAttribute("aria-hidden", "true");
    this.#applyAlignment(img, frame);
    this.#stage.insertBefore(img, this.#stage.querySelector(".bc-turn__loading"));
    frame.el = img;
    return img;
  }

  /** Uniform scale + shift from the photo audit (no stretching). @param {HTMLElement} el @param {Frame} frame */
  #applyAlignment(el, frame) {
    const t = Array.isArray(frame.t) ? frame.t : null;
    if (!t) return;
    el.style.setProperty("--bc-s", String(clamp(Number(t[0]) || 1, 0.8, 1.25)));
    el.style.setProperty("--bc-x", `${(Number(t[1]) || 0) * 100}%`);
    el.style.setProperty("--bc-y", `${(Number(t[2]) || 0) * 100}%`);
  }

  /** @param {Frame} frame @param {'lo' | 'hi'} kind @returns {Promise<void>} */
  #load(frame, kind) {
    if (frame[kind]) return /** @type {Promise<void>} */ (frame[kind]);
    const url = this.#url(frame, kind);
    let promise;
    if (kind === "lo") {
      // Straight into the (hidden) frame element.
      const el = this.#frameEl(frame);
      promise = new Promise((resolve, reject) => {
        el.addEventListener("load", () => resolve(undefined), { once: true });
        el.addEventListener("error", () => reject(new Error(`bc-turn: ${url}`)), { once: true });
        el.src = url;
      }).then(() => {
        if (!frame.has) frame.has = "lo";
        el.dataset.frameLoaded = frame.has || "lo";
      });
    } else {
      // Fetched on the side, then swapped in, so a frame never goes blank while it sharpens.
      promise = new Promise((resolve, reject) => {
        const probe = new Image();
        probe.decoding = "async";
        probe.onload = () => resolve(undefined);
        probe.onerror = () => reject(new Error(`bc-turn: ${url}`));
        probe.src = url;
      }).then(() => {
        const el = this.#frameEl(frame);
        el.src = url;
        frame.has = "hi";
        el.dataset.frameLoaded = "hi";
      });
    }
    frame[kind] = promise;
    promise
      .then(() => {
        this.#progress();
        if (this.#frames[this.#index] === frame) this.#paint(false);
      })
      .catch(() => this.#drop(frame));
    return promise;
  }

  /** Frames ordered by distance from the one on screen. */
  #nearestFirst() {
    const n = this.#frames.length;
    const distance = (i) => {
      const d = Math.abs(i - this.#index);
      return this.#full ? Math.min(d, n - d) : d;
    };
    return this.#frames.map((frame, i) => ({ frame, d: distance(i) })).sort((a, b) => a.d - b.d).map((x) => x.frame);
  }

  /** @param {number} [limit] - load this many frames, nearest the current one first (all by default) */
  #prime(limit = Infinity) {
    if (!this.#prepared || this.#primed >= Math.min(limit, this.#frames.length)) return;
    this.#primed = Math.min(limit, this.#frames.length);
    // The first frame stays lazy until now, so it never competes with the page's main image.
    this.#stage.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = "eager"));
    for (const frame of this.#nearestFirst().slice(0, this.#primed)) this.#load(frame, "lo");
  }

  #upgrade() {
    if (this.#upgraded || !this.#prepared) return;
    this.#upgraded = true;
    this.#prime();
    // Two at a time, nearest first, each after its small copy.
    const queue = this.#nearestFirst().filter((frame) => frame.has !== "hi");
    const next = () => {
      const frame = queue.shift();
      if (!frame) return;
      if (!this.#frames.includes(frame)) return next();
      (frame.lo || Promise.resolve())
        .then(() => this.#load(frame, "hi"))
        .catch(() => {})
        .finally(next);
    };
    next();
    next();
  }

  #progress() {
    const n = this.#frames.length;
    const done = this.#frames.filter((frame) => frame.has).length;
    this.style.setProperty("--bc-progress", String(done / n));
    this.classList.toggle("is-loading", this.#touched && done < n);
  }

  /** @param {Frame} frame */
  #drop(frame) {
    const i = this.#frames.indexOf(frame);
    if (i === -1) return;
    this.#frames.splice(i, 1);
    frame.el?.remove();
    this.querySelectorAll(".bc-turn__dots i")[i]?.remove();
    if (this.#frames.length < 2) {
      this.#fail(new Error("bc-turn: not enough frames loaded"));
      return;
    }
    if (this.#index >= i && this.#index > 0) this.#index -= 1;
    this.#paint(false);
    this.#updateUi();
  }

  /* ------------------------------------------------------------- showing */

  /** @param {number} target @param {boolean} fade */
  #show(target, fade) {
    const n = this.#frames.length;
    const i = this.#full ? ((target % n) + n) % n : clamp(target, 0, n - 1);
    if (i === this.#index) return;
    this.#index = i;
    const frame = this.#frames[i];
    this.#load(frame, "lo");
    if (this.#upgraded) this.#load(frame, "hi");
    this.#paint(fade);
    this.#updateUi();
  }

  /** Puts the current frame on top once it has an image; until then the last one stays. */
  #paint(fade) {
    const frame = this.#frames[this.#index];
    const el = frame?.el;
    if (!el || !frame.has) return;
    const current = this.#stage.querySelector(".bc-turn__frame.is-active");
    if (current === el) return;

    const animate = fade && !reducedMotion() && this.#frames.length < 12;
    clearTimeout(this.#fadeTimer);
    this.classList.toggle("is-fading", animate);
    this.#stage.querySelectorAll(".bc-turn__frame.is-prev").forEach((node) => node.classList.remove("is-prev"));
    if (current) {
      current.classList.remove("is-active");
      if (animate) current.classList.add("is-prev");
    }
    el.classList.add("is-active");
    if (animate) {
      this.#fadeTimer = window.setTimeout(() => {
        this.classList.remove("is-fading");
        current?.classList.remove("is-prev");
      }, FADE_MS + 40);
    }
  }

  #updateUi() {
    const n = this.#frames.length;
    const i = this.#index;
    const frame = this.#frames[i];
    if (!frame) return;
    this.#stage.setAttribute("aria-valuemax", String(n));
    this.#stage.setAttribute("aria-valuenow", String(i + 1));
    this.#stage.setAttribute("aria-valuetext", `View ${i + 1} of ${n}: ${frame.alt}`);
    this.querySelectorAll(".bc-turn__dots i").forEach((dot, k) => dot.classList.toggle("is-on", k === i));
    const [prev, next] = this.querySelectorAll("[data-step]");
    if (prev && next && !this.#full) {
      /** @type {HTMLButtonElement} */ (prev).disabled = i === 0;
      /** @type {HTMLButtonElement} */ (next).disabled = i === n - 1;
    }
    const zoom = this.querySelector("[data-bc-turn-zoom]");
    const zoomWrap = /** @type {HTMLElement | null} */ (zoom?.closest(".media-gallery__zoom-dialog-button"));
    if (zoom && zoomWrap) {
      zoomWrap.hidden = !frame.z;
      if (frame.z) zoom.setAttribute("data-media-id", String(frame.z));
    }
  }

  /* --------------------------------------------------------- interaction */

  /** @param {boolean} [strong] - a touch, click or focus (not just a glance) */
  #interest(strong = false) {
    this.#prime();
    if (strong) {
      this.#touched = true;
      this.#stopSpin();
      this.#upgrade();
    }
  }

  /** First real turn: the "Drag to turn" words give way to the position dots. */
  #turned() {
    this.classList.add("has-turned");
  }

  /** Pixels of drag per frame: a full turn spans the width; a partial one about 70% of it. */
  #pxPerFrame() {
    const width = this.#stage.clientWidth || 400;
    const n = this.#frames.length;
    return this.#full ? Math.max(12, width / n) : clamp((width * 0.7) / Math.max(1, n - 1), 30, 150);
  }

  /** @param {PointerEvent} event */
  #onDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    this.#interest(true);
    const n = this.#frames.length;
    this.#drag = {
      id: event.pointerId,
      x: event.clientX,
      i: this.#index,
      moved: false,
      fromEnd: this.#full ? 0 : this.#index === 0 ? -1 : this.#index === n - 1 ? 1 : 0,
    };
    try {
      this.#stage.setPointerCapture(event.pointerId);
    } catch (_) {
      /* not supported: plain pointer events still work */
    }
  };

  /** @param {PointerEvent} event */
  #onMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      this.classList.add("is-dragging");
      this.#turned();
    }
    // Dragging right shows more of the kit's left side (frames run left side -> right side).
    const target = drag.i - Math.round(dx / this.#pxPerFrame());

    // Already on the last view and still dragging the same way: hand over to the gallery.
    const n = this.#frames.length;
    const pastEnd = (drag.fromEnd === 1 && target > n - 1) || (drag.fromEnd === -1 && target < 0);
    if (pastEnd && Math.abs(dx) > 60) {
      const swiper = /** @type {any} */ (this.closest("carousel-slider"))?.swiperInstance;
      this.#onUp(event);
      if (drag.fromEnd === 1) swiper?.slideNext?.();
      else swiper?.slidePrev?.();
      return;
    }
    this.#show(target, true);
  };

  /** @param {PointerEvent} event */
  #onUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.id) return;
    this.#drag = null;
    this.classList.remove("is-dragging");
    try {
      this.#stage.releasePointerCapture(event.pointerId);
    } catch (_) {
      /* already released */
    }
  };

  /** Slider keys: arrows, Home/End, Page Up/Down. @param {KeyboardEvent} event */
  #onKey = (event) => {
    const n = this.#frames.length;
    const steps = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 2, PageDown: -2 };
    let target;
    if (event.key in steps) target = this.#index + steps[/** @type {keyof typeof steps} */ (event.key)];
    else if (event.key === "Home") target = 0;
    else if (event.key === "End") target = n - 1;
    else return;
    event.preventDefault();
    event.stopPropagation();
    this.#interest(true);
    this.#turned();
    this.#show(target, true);
  };

  /* ------------------------------------------------------------ auto-turn */

  /** One gentle pass when first on screen (product setting), never with reduced motion. */
  #maybeSpin() {
    if (this.dataset.autospin !== "true" || this.#spun || this.#touched || reducedMotion()) return;
    this.#spun = true;
    this.#prime();
    Promise.allSettled(this.#frames.map((frame) => this.#load(frame, "lo"))).then(() => {
      this.#spinTimer = window.setTimeout(() => {
        if (!this.#visible || this.#touched || document.hidden) return;
        const n = this.#frames.length;
        const start = this.#index;
        /** @type {number[]} */
        let path = [];
        if (this.#full) {
          for (let k = 1; k <= n; k++) path.push(start + k);
        } else {
          for (let k = start + 1; k < n; k++) path.push(k);
          for (let k = n - 2; k >= 0; k--) path.push(k);
          for (let k = 1; k <= start; k++) path.push(k);
        }
        const delay = this.#full ? Math.max(110, 2600 / n) : 420;
        const step = () => {
          if (this.#touched || !this.#visible || document.hidden) return;
          const next = path.shift();
          if (next === undefined) return;
          this.#show(next, true);
          this.#spinTimer = window.setTimeout(step, delay);
        };
        step();
      }, 900);
    });
  }

  #stopSpin() {
    clearTimeout(this.#spinTimer);
    this.#spinTimer = 0;
  }

  /* -------------------------------------------------------------- fallback */

  /** Anything wrong: remove the slide and its thumbnail; the normal gallery carries on. */
  #fail(_error) {
    this.#stopSpin();
    this.#ac?.abort();
    this.#io?.disconnect();
    const gallery = this.closest("media-gallery");
    const carousel = /** @type {any} */ (gallery?.querySelector("carousel-slider"));
    this.closest(".bc-turn-slide")?.remove();
    gallery?.querySelector(".bc-turn-thumb")?.remove();
    gallery?.querySelector("[data-bc-turn-chip]")?.remove();
    try {
      carousel?.swiperInstance?.update?.();
      carousel?.thumbnailSwiper?.update?.();
    } catch (_) {
      /* the gallery re-reads its slides on its next update */
    }
  }
}

if (!customElements.get("bc-turn-viewer")) {
  customElements.define("bc-turn-viewer", BcTurnViewer);
}
